<template>
    <v-card>
        <v-form
                ref="blob-form"
                lazy-validation
        >
            <v-card-title>
                {{ editableBlob.name }}
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12">
                        <v-switch
                              v-model="editableBlob.is_private"
                              :label="$t('form.general.private.label')"
                              color="error"
                        ></v-switch>
                    </v-col>
                    <v-col cols="12">
                        <v-text-field
                              v-model="editableBlob.name"
                              :label="$t('form.general.name.label')"
                              :error-messages="nameErrors"
                              :counter="20"
                              required
                              @blur="$v.editableBlob.name.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                        <v-textarea
                              v-model="editableBlob.description"
                              :label="$t('form.blob.description.label')"
                              :placeholder="$t('form.blob.description.placeholder')"
                              :error-messages="descriptionErrors"
                              required
                              @blur="$v.editableBlob.description.$touch()"
                        ></v-textarea>
                    </v-col>
                    <v-col cols="6" v-if="isNew">
                        <v-select
                              v-model="editableBlob.quadrant"
                              :items="quadrants"
                              :label="$t('form.blob.quadrants.label')"
                        ></v-select>
                    </v-col>
                    <v-col cols="6" v-if="isNew">
                        <v-select
                              v-model="editableBlob.sector"
                              :items="sectors"
                              :label="$t('form.blob.sectors.label')"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" v-if="isNew">
                        <v-slider
                              v-model="editableBlob.sector_progress"
                              :label="$t('form.blob.sector_progress.label')"
                              step="1"
                              max="10"
                              thumb-label="always"
                              ticks
                        ></v-slider>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    text
                    @click="close"
                >
                    {{ $t('controls.cancel') }}
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn icon right color="error" v-if="!isNew" @click="openDeleteDialog">
                    <v-icon>mdi-delete</v-icon>
                </v-btn>

                <v-btn
                    dark
                    @click="save"
                >
                   <span v-if="isNew">
                        {{ $t('controls.create') }}
                    </span>
                    <span v-else>
                        {{ $t('controls.save') }}
                    </span>
                </v-btn>
            </v-card-actions>
        </v-form>
    </v-card>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { validationMixin } from 'vuelidate'
  import { maxLength, minLength, required } from 'vuelidate/lib/validators'

  export default {
    name: 'BlobForm',
    mixins: [validationMixin],

    validations: {
      editableBlob: {
        name: {
          required,
          minLength: minLength(4),
          maxLength: maxLength(20)
        },
        description: {
          required,
          minLength: minLength(10),
        }
      }
    },
    props: {
      blob: {
        type: Object,
        required: true
      },
      cancel: {
        type: Boolean,
        default: false
      }
    },

    data: function () {
      return {
        blobBlueprint: {
          name: '',
          description: '',
          quadrant: 0,
          sector: 3,
          sector_progress: 0
        },
        editableBlob: {}
      }
    },

    computed: {
      ...mapGetters({
        radar: 'radars/getCurrentRadar',
      }),

      nameErrors () {
        const errors = []
        if (!this.$v.editableBlob.name.$dirty) return errors
        !this.$v.editableBlob.name.required && errors.push(this.$t('form.radar.name.required'))
        !this.$v.editableBlob.name.minLength && errors.push(this.$t('form.radar.name.min-length'))
        !this.$v.editableBlob.name.maxLength && errors.push(this.$t('form.radar.name.max-length'))
        return errors
      },

      descriptionErrors () {
        const errors = []
        if (!this.$v.editableBlob.description.$dirty) return errors
        !this.$v.editableBlob.description.required && errors.push(this.$t('form.radar.description.required'))
        !this.$v.editableBlob.description.minLength && errors.push(this.$t('form.radar.description.min-length'))
        return errors
      },

      quadrants () {
        return [
          { text: this.radar.quadrant_0, value: 0 },
          { text: this.radar.quadrant_1, value: 1 },
          { text: this.radar.quadrant_2, value: 2 },
          { text: this.radar.quadrant_3, value: 3 },
        ]
      },
      sectors () {
        return [
          { text: this.radar.sector_3, value: 3 },
          { text: this.radar.sector_2, value: 2 },
          { text: this.radar.sector_1, value: 1 },
          { text: this.radar.sector_0, value: 0 },
        ]
      },
      isNew () {
        return this.editableBlob.id === undefined
      }
    },
    mounted () {
      this.editableBlob = Object.assign(this.blobBlueprint, this.blob)
    },
    methods: {
      save () {
        this.$v.$touch()

        if (!this.$v.$invalid) {
          let dispatchMethod = this.isNew ? 'blobs/createBlob' : 'blobs/updateBlob'

          this.$store.dispatch(dispatchMethod, this.editableBlob)
            .then((response) => {
              if (this.isNew) {
                // reset form
                this.editableBlob = {}
                this.$refs['blob-form'].reset()
                this.$v.$reset()
                // set as blob
                this.$store.commit('blobs/setCurrentBlobId', response.data.blob.id)

                this.$store.commit('notification/newNotification', {
                  text: this.$t('form.blob.created')
                })
              } else {
                this.$store.commit('notification/newNotification', {
                  text: this.$t('form.blob.updated')
                })
              }

              this.$emit('blobIsProcessed')
            })
        }
      },

      close () {
        this.$emit('blobIsCanceled')
        this.editableBlob = {}
        this.$refs['blob-form'].reset()
        this.$v.$reset()
      },

      openDeleteDialog () {
        const deletedText = this.$t('form.blob.removed')

        this.$store.commit('prompt/newPrompt', {
          title: this.$t('dialog.delete.title') + ' ' + this.editableBlob.name,
          text: this.$t('dialog.delete.blob.text'),
          execCallback: function () {
            // reset current blob id
            this.$store.commit('blobs/setCurrentBlobId', 0)

            this.$store.dispatch('blobs/removeBlob', this.editableBlob)
              .then(() => {
                // close form
                this.$emit('blobIsProcessed')
                // go back to quadrant view
                this.$emit('openQuadrant', this.editableBlob.quadrant)

                // trigger notification
                this.$store.commit('notification/newNotification', {
                  text: deletedText
                })
              })
          }.bind(this)
        })
      }
    },
  }
</script>
